<script setup lang="ts">
    import Slider from '@vueform/slider';
    import { defineComponent, h, type PropType } from 'vue';

    const props = defineProps<{
        filterOpen: boolean;
        size: string;
        bio: boolean;
        minPrice: number;
        maxPrice: number;
        minPriceLimit: number;
        maxPriceLimit: number;
        selectedCategoryId: string;
        categoriesTree: CategoryNode[];
        labelMap: Record<string, string>;
        labelsReady: boolean;
        productsLength: number;
    }>();

    const emit = defineEmits<{
        (e: 'show-filter'): void;
        (e: 'update-category', id: string): void;
        (e: 'update-size', size: string): void;
        (e: 'update-min-price', value: number): void;
        (e: 'update-max-price', value: number): void;
        (e: 'update-bio', value: boolean): void;
    }>();

    const { t } = useI18n();

    type CategoryNode = { id: string; label: string; children: CategoryNode[] };

    const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
    const bioRef = ref(props.bio);
    const priceLimits = ref([props.minPrice, props.maxPrice]);

    const categoriesOpen = ref(false);
    const sizesOpen = ref(false);
    const mountedFilterOpen = ref(false);
    onMounted(() => {
        mountedFilterOpen.value = props.filterOpen;
    });

    const showCategories = () => {
        categoriesOpen.value = !categoriesOpen.value;
    };
    const showSizes = () => {
        sizesOpen.value = !sizesOpen.value;
    };

    const CategoryBranch = defineComponent({
        name: 'CategoryBranch',
        props: {
            nodes: { type: Array as PropType<CategoryNode[]>, required: true },
            selectedId: { type: String, required: true },
            depth: { type: Number, default: 0 }
        },
        emits: ['pick'],
        setup(product, { emit }) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const renderNode = (node: CategoryNode, depth: number): any[] => {
                const btn = h(
                    'button',
                    {
                        type: 'button',
                        class: [depth > 0 ? 'sub-category' : '', node.id === product.selectedId ? '--active' : ''],
                        'aria-pressed': node.id === product.selectedId ? 'true' : 'false',
                        onClick: () => emit('pick', node.id)
                    },
                    node.label
                );

                const children = node.children?.flatMap((category) => renderNode(category, depth + 1)) ?? [];
                return [btn, ...children];
            };

            return () => product.nodes.flatMap((node) => renderNode(node, product.depth || 0));
        }
    });
</script>

<template>
    <div class="filter-container">
        <h2 class="desktop-title">{{ t('listing.available') }}</h2>

        <Transition name="filter">
            <div
                v-if="mountedFilterOpen"
                class="filter"
            >
                <div class="modal-header">
                    <h2>{{ t('listing.header') }}</h2>

                    <button
                        class="close-btn"
                        @click="emit('show-filter')"
                    >
                        <img
                            src="~/assets/icons/close.svg"
                            alt="close"
                        />
                        <span class="sr-only">{{ t('listing.close') }}</span>
                    </button>
                </div>

                <div class="filter-wrapper">
                    <button
                        class="custom-select-button"
                        @click="showCategories"
                    >
                        <span class="select-title">{{ t('listing.categories') }}</span>
                        <div
                            class="select-value"
                            :class="{ '--active': categoriesOpen }"
                        >
                            <span class="select-value">
                                {{
                                    selectedCategoryId === 'all'
                                        ? t('listing.rootCategoryName')
                                        : labelMap[selectedCategoryId] || t('listing.rootCategoryName')
                                }}
                            </span>
                            <img
                                class="icon"
                                src="~/assets/icons/chevron-right-black.svg"
                                alt="Filter"
                                aria-hidden="true"
                            />
                        </div>
                    </button>

                    <Transition name="filter">
                        <div
                            v-if="categoriesOpen"
                            class="custom-select-modal"
                        >
                            <button
                                class="back-button"
                                @click="showCategories"
                            >
                                <img
                                    src="~/assets/icons/chevron-right-black.svg"
                                    alt="close"
                                />
                                <span>{{ t('general.back') }}</span>
                            </button>

                            <button
                                :class="selectedCategoryId === 'all' ? '--active' : ''"
                                @click="
                                    emit('update-category', 'all');
                                    showCategories();
                                "
                            >
                                {{ t('listing.rootCategoryName') }}
                            </button>

                            <template v-if="props.labelsReady">
                                <CategoryBranch
                                    :nodes="categoriesTree"
                                    :selected-id="selectedCategoryId"
                                    @pick="
                                        (id) => {
                                            emit('update-category', id);
                                            showCategories();
                                        }
                                    "
                                />
                            </template>
                        </div>
                    </Transition>

                    <button
                        class="custom-select-button"
                        @click="showSizes"
                    >
                        <span class="select-title">{{ t('listing.size') }}</span>

                        <div
                            class="select-value"
                            :class="{ '--active': sizesOpen }"
                        >
                            <span>{{ size ? size : t('listing.allSizes') }}</span>
                            <img
                                class="icon"
                                src="~/assets/icons/chevron-right-black.svg"
                                alt="Filter"
                                aria-hidden="true"
                            />
                        </div>
                    </button>

                    <Transition name="filter">
                        <div
                            v-if="sizesOpen"
                            class="custom-select-modal"
                        >
                            <button
                                class="back-button"
                                @click="showSizes"
                            >
                                <img
                                    src="~/assets/icons/chevron-right-black.svg"
                                    alt="close"
                                />
                                <span>{{ t('general.back') }}</span>
                            </button>

                            <button
                                :class="size ? '' : '--active'"
                                @click="(emit('update-size', ''), showSizes())"
                            >
                                {{ t('listing.allSizes') }}
                            </button>

                            <button
                                v-for="(value, index) in sizes"
                                :key="index"
                                :class="value === size ? '--active' : ''"
                                @click="(emit('update-size', value), showSizes())"
                            >
                                {{ value }}
                            </button>
                        </div>
                    </Transition>

                    <div class="price-element">
                        <label>{{ t('listing.price') }}</label>

                        <div class="price-input-wrapper">
                            <div class="price-input">
                                <div class="price-field">
                                    <input
                                        v-if="priceLimits[0] !== undefined"
                                        v-model.number="priceLimits[0]"
                                        type="number"
                                        class="min-input"
                                        placeholder="Mindestpreis"
                                        @change="emit('update-min-price', priceLimits[0])"
                                    />
                                </div>
                                <div class="price-field">
                                    <span>{{ t('listing.to') }}</span>
                                    <input
                                        v-if="priceLimits[1] !== undefined"
                                        v-model.number="priceLimits[1]"
                                        type="number"
                                        class="max-input"
                                        placeholder="Höchstpreis"
                                        @change="emit('update-max-price', priceLimits[1])"
                                    />
                                </div>
                            </div>
                            <Slider
                                v-if="priceLimits[0] !== undefined && priceLimits[1] !== undefined"
                                v-model="priceLimits"
                                :max="maxPriceLimit"
                                :min="minPriceLimit"
                                :format="{ suffix: '€', decimals: 0 }"
                                tooltip-position="bottom"
                                @update="
                                    emit('update-min-price', priceLimits[0]);
                                    emit('update-max-price', priceLimits[1]);
                                "
                            />
                        </div>
                    </div>

                    <div class="checkbox-element --no-border-bottom">
                        <label for="bio-select">{{ t('listing.bio') }}:</label>

                        <input
                            id="bio-select"
                            v-model="bioRef"
                            type="checkbox"
                            @change="emit('update-bio', bioRef)"
                        />
                    </div>
                </div>

                <div class="found-products">
                    <PrimaryButton
                        :button-text="productsLength + ' Produkte gefunden'"
                        product-button
                        has-icon
                        class="found-products-button"
                        @click="emit('show-filter')"
                    />
                </div>
            </div>
        </Transition>
    </div>
</template>

<!-- Removed scope so CategoryBranch is affected by styling -->
<style lang="scss">
    @import '~/assets/scss/plp/plp-filter.scss';
</style>

<style src="@vueform/slider/themes/default.css"></style>
